import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SingleToolHeader from '@quicktools/shared-components/components/common/SingleToolHeader/SingleToolHeader';
import { SectionWithStickyInput, WithBreadcrumb } from '@quicktools/shared-components';
import { isMobileOnly } from 'react-device-detect';
import LayoutForTextApps from '@quicktools/shared-components/components/common/LayoutForTextApps/LayoutForTextApps';
import NexHead from '@quicktools/shared-components/components/common/NextHead/NextHead';
import getBreadcrumbSublinks from '@quicktools/shared-components/utils/breadcrumbSubLinks';
import { IBreadcrumb } from '@quicktools/shared-components/components/core/Breadcrumb/types';
import useSnackbarStore from '@quicktools/shared-components/hooks/snackbar';
import SnackbarList from '@quicktools/shared-components/components/core/SnackbarList';

import { mapLastSublink, scrollToSearchInput } from '../../helpers';
import Symbols from '../Symbols/Symbols';
import Editor from '../Editor';
import FilterModal from '../FilterModal';
import CategoryFilter from '../CategoryFilter';
import useApp from '../../store/hooks/useApp';
import analytics from '../../services/analytics';
import { TOOL_NAME } from '../../configs';

import { IApp } from './types';
import useStyles from './styles';

export default function CategoryPage({ symbols, category, children }: IApp) {
  const { t } = useTranslation();
  const styles = useStyles();

  const seoTitle = t(`categories.${category}.seoTitle`);
  const seoDescription = t(`categories.${category}.seoDescription`);
  const title = t(`categories.${category}.title`);
  const description = t(`categories.${category}.description`);
  const searchPlaceholder = t(`categories.${category}.search`);
  const canonicalPath = category === 'index' ? 'text/symbols/' : `text/symbols/${category}/`;
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumb[]>([]);
  const snackItems = useSnackbarStore((state) => state.snackItems);
  const closeSnack = useSnackbarStore((state) => state.snackbarClose);
  const [stickyOffset, setStickyOffset] = useState(0);

  const {
    showModal,
    searchFound,
    currentSymbols,
    onChooseSymbol,
    onChangeCategory,
    onChangeSearchText,
    onToggleFilterModal,
    debouncedSearchTerm,
    onSearchInAllCategories,
  } = useApp(symbols);

  useEffect(() => {
    analytics.pageOpen();
  }, []);

  useEffect(() => {
    const sublinks = getBreadcrumbSublinks(canonicalPath);
    const lastSublink = sublinks[sublinks.length - 1];
    const mappedLastSublink = mapLastSublink(lastSublink);
    sublinks.pop();
    sublinks.push(mappedLastSublink);
    setBreadcrumbItems(sublinks);
  }, [canonicalPath]);

  return (
    <>
      <NexHead title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} />
      <div className={styles.root}>
        <SnackbarList
          className={styles.notifications}
          snackItems={snackItems}
          onClose={closeSnack}
        />
        <SingleToolHeader analytics={analytics} toolName={TOOL_NAME} className="header" />

        <WithBreadcrumb items={breadcrumbItems} />

        <LayoutForTextApps
          stickyOffset={stickyOffset}
          stickyInputChild={
            <SectionWithStickyInput
              onChange={onChangeSearchText}
              onFilterClick={onToggleFilterModal}
              onInputClick={() => {
                analytics.textBoxClick();
                isMobileOnly && scrollToSearchInput();
              }}
              primaryText={title}
              placeholder={searchPlaceholder}
              secondaryText={description}
              onStickyOffsetChange={setStickyOffset}
              defaultValue={debouncedSearchTerm}
              hasSearchIcon
              autoFocus
            />
          }
          sidebarChild={
            <>
              <CategoryFilter
                heading="Select Category"
                onSelectItem={onChangeCategory}
                selectedCategory={category}
              />
            </>
          }
          listingBarChild={
            <>
              {debouncedSearchTerm && (
                <div className={styles.resultsMsg}>
                  Search results for{' '}
                  <span className="color--primary">
                    “{debouncedSearchTerm}” ({searchFound})
                  </span>
                </div>
              )}

              <div></div>
            </>
          }
          listingChild={
            <Symbols
              symbolsList={currentSymbols}
              chooseSymbol={onChooseSymbol}
              searchInAllCategories={onSearchInAllCategories}
              displayCategory={debouncedSearchTerm.length === 0}
              stickyOffset={stickyOffset}
            />
          }
        >
          {children}
        </LayoutForTextApps>

        <Editor />

        {showModal && (
          <FilterModal
            changeCategory={onChangeCategory}
            closeModal={onToggleFilterModal}
            selectedCategory={category}
          />
        )}
      </div>
    </>
  );
}
